import { LANGUAGE_OPTIONS } from './enums';
import { toast } from 'react-toastify';

const mapboxToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

export const getDefaultLocale = () => {
  const defaultLocale =
    typeof window !== 'undefined' && window.localStorage.getItem('selectedLanguage');

  return defaultLocale || LANGUAGE_OPTIONS.FR;
};

export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

export const isObjectLength = obj => {
  return (Object.keys(obj) && Object.keys(obj).length > 0) ?? false;
};


export const getPayInTotal = tx => {
  return tx?.attributes?.payinTotal?.amount ?? 0;
};

export const sortCountriesWithPriority = countriesArray => {
  // Priority list of country codes
  const priorityCodes = ['US', 'GB'];

  // Splitting the array into priority and normal countries
  const priorityCountries = countriesArray.filter(country => priorityCodes.includes(country.code));
  const otherCountries = countriesArray.filter(country => !priorityCodes.includes(country.code));

  // Optional: Sort otherCountries alphabetically by label if needed
  otherCountries.sort((a, b) => a.label?.localeCompare(b.label));

  // Concatenating the priority countries with the sorted other countries
  return [...priorityCountries, ...otherCountries];
};

export const getDistanceBetweenPoints = async (start, end) => {
  console.log('start, end', start, end);

  const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${start.lng},${start.lat};${end.lng},${end.lat}?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}`;
  try {
    const response = await fetch(url);
    const data = await response.json();
    if (data.routes && data.routes.length > 0) {
      const distance = data.routes[0].distance; // Distance in meters
      return distance;
    } else {
      throw new Error('No routes found');
    }
  } catch (error) {
    console.error('Error getting distance:', error);
  }
};

export const getStaticDistanceBetweenPoints = (start, end) => {
  const distance = 1000;
  try {
    return distance;
  } catch (error) {
    console.error('Error getting distance:', error);
  }
};

/**
 * Display a toast notification based on the message type.
 *
 * @param {string} type The type of toast to display ('success' or 'error').
 * @param {string} message The message to display in the toast.
 * @param {Object} [options] Additional options for customizing the toast appearance and behavior.
 */
export const showToaster = (type, message, options = {}) => {
  const defaultOptions = {
    position: 'top-center',
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'light',
    ...options, // Merge any additional options passed to the function
  };

  // Display the toast based on the type
  if (type === 'success') {
    toast.success(message, defaultOptions);
  } else if (type === 'error') {
    toast.error(message, defaultOptions);
  } else {
    console.warn('showToast called with invalid type. Expected "success" or "error".');
  }
};

// Function to calculate the distance between two points using the Haversine formula
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    0.5 -
    Math.cos(dLat) / 2 +
    (Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * (1 - Math.cos(dLon))) /
      2;

  return R * 2 * Math.asin(Math.sqrt(a));
}

// Function to get the distance between two addresses using Mapbox
export async function getDistanceBetweenAddresses(address1, address2) {
  // Geocode the first address
  const response1 = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
      address1
    )}.json?access_token=${mapboxToken}`
  );
  const data1 = await response1.json();
  const [lon1, lat1] = data1.features[0].geometry.coordinates;

  // Geocode the second address
  const response2 = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
      address2
    )}.json?access_token=${mapboxToken}`
  );
  const data2 = await response2.json();
  const [lon2, lat2] = data2.features[0].geometry.coordinates;

  // Calculate the distance between the two points
  const distance = calculateDistance(lat1, lon1, lat2, lon2);

  return distance;
}

function getCenterPoint(bounds) {
  if (typeof bounds === 'string') {
    const parsedBounds = bounds.split(',').map(Number);
    if (parsedBounds.length === 4) {
      const [neLat, neLng, swLat, swLng] = parsedBounds;
      const lat = (neLat + swLat) / 2;
      const lon = (neLng + swLng) / 2;
      return { lat, lon };
    }
  } else if (bounds._sdkType === 'LatLngBounds') {
    const lat = (bounds.ne.lat + bounds.sw.lat) / 2;
    const lon = (bounds.ne.lng + bounds.sw.lng) / 2;
    return { lat, lon };
  } else {
    throw new Error('Invalid bounds format');
  }
}

// Function to get the distance between two addresses using Mapbox
export const getDrivingDistanceBetweenBounds = async (address1Bounds, address2Bounds) => {
  // Helper function to extract the center point of the bounds

  // Extract center points from the bounds
  const { lat: lat1, lon: lon1 } = getCenterPoint(address1Bounds);
  const { lat: lat2, lon: lon2 } = getCenterPoint(address2Bounds);
  // Use the Directions API to get the driving distance
  const response = await fetch(
    `https://api.mapbox.com/directions/v5/mapbox/driving/${lon2},${lat2};${lon1},${lat1}?alternatives=true&geometries=geojson&language=en&overview=full&steps=true&access_token=${mapboxToken}`
  );
  const data = await response.json();
  const distance = data.routes[0].distance / 1000; // distance in kilometers

  return distance;
};


export function extractStringBeforeCity(shippingAddress) {
  const fullAddress = shippingAddress?.location?.selectedPlace?.address || ''; // Get the full address
  const city = shippingAddress?.city || ''; // Get the city

  if (!fullAddress || !city) return null; // Check if both fullAddress and city are provided

  const cityIndex = fullAddress.indexOf(city); // Find the index of the city in the address
  if (cityIndex === -1) return null; // Return null if the city is not found in the address

  return fullAddress.substring(0, cityIndex).trim(); // Extract and return the substring before the city
}

export function getStripeInfo(currentUser) {
  if (!currentUser || currentUser.type !== 'currentUser') {
    return {
      stripeCustomerId: null,
      stripePaymentMethodId: null,
    };
  }

  const stripeCustomerId =
    currentUser.stripeCustomer?.attributes?.stripeCustomerId || null;

  const stripePaymentMethodId =
    currentUser.stripeCustomer?.defaultPaymentMethod?.attributes
      ?.stripePaymentMethodId || null;

  return {
    stripeCustomerId,
    stripePaymentMethodId,
  };
}